<template>
  <component
    :is="componentTag"
    :class="buttonClasses"
    :disabled="disabled || loading"
    :type="type"
    v-bind="linkAttrs"
    @click="handleClick"
  >
    <span v-if="loading" class="mr-2">
      <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </span>
    <slot />
  </component>
</template>

<script setup lang="ts">
import type { ButtonProps } from "~/types";

interface Props extends ButtonProps {
  tag?: string;
  href?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "primary",
  size: "base",
  tag: "button",
  type: "button",
  disabled: false,
  loading: false,
  fullWidth: false,
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const componentTag = computed(() => {
  if (props.href) return resolveComponent("NuxtLink");
  return props.tag;
});

const linkAttrs = computed(() => {
  return props.href ? ({ to: props.href } as any) : ({} as any);
});

const buttonClasses = computed(() => {
  const baseClasses = [
    "inline-flex",
    "items-center",
    "justify-center",
    'font-["Outfit"]',
    "font-medium",
    "rounded-[30px]",
    "transition-all",
    "duration-200",
    "focus:outline-none",
    "focus:ring-2",
    "focus:ring-offset-2",
    "overflow-hidden",
  ];

  // Size variants - matching exact Figma specs
  const sizeClasses = {
    sm: ["px-[14px]", "py-[7px]", "text-[12px]", "leading-[16px]"],
    base: ["px-[17px]", "py-[9px]", "text-[14px]", "leading-[20px]"],
    lg: ["px-[20px]", "py-[12px]", "text-[16px]", "leading-[24px]"],
  };

  // Color variants
  const variantClasses = {
    primary: [
      "bg-[#d0a444]",
      "text-white",
      "border-none",
      "hover:bg-[#B8922F]",
      "focus:ring-[#d0a444]/50",
      "disabled:bg-[#d0a444]/50",
      "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
    ],
    secondary: [
      "bg-white",
      "text-[#374151]",
      "border",
      "border-gray-300",
      "hover:bg-gray-50",
      "focus:ring-gray-500/50",
      "disabled:bg-gray-100",
      "disabled:text-gray-400",
      "disabled:border-gray-300",
      "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
    ],
    outline: [
      "bg-white",
      "text-[#374151]",
      "border",
      "border-gray-300",
      "hover:bg-gray-50",
      "focus:ring-gray-500/50",
      "disabled:bg-gray-100",
      "disabled:text-gray-400",
      "disabled:border-gray-300",
      "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
    ],
    ghost: [
      "bg-transparent",
      "text-gray-700",
      "border-transparent",
      "hover:bg-gray-100",
      "focus:ring-gray-500/50",
      "disabled:text-gray-400",
    ],
    danger: [
      "bg-red-600",
      "text-white",
      "border-none",
      "hover:bg-red-700",
      "focus:ring-red-500/50",
      "disabled:bg-red-600/50",
      "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
    ],
    "danger-outline": [
      "bg-white",
      "text-red-600",
      "border",
      "border-red-300",
      "hover:bg-red-50",
      "hover:border-red-400",
      "focus:ring-red-500/50",
      "disabled:text-red-300",
      "disabled:border-red-200",
      "shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
    ],
  };

  // Width classes
  const widthClasses = props.fullWidth ? ["w-full"] : [];

  // Disabled/loading classes
  const stateClasses =
    props.disabled || props.loading
      ? ["cursor-not-allowed", "opacity-50"]
      : ["cursor-pointer"];

  return [
    ...baseClasses,
    ...sizeClasses[props.size],
    ...variantClasses[props.variant],
    ...widthClasses,
    ...stateClasses,
  ].join(" ");
});

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit("click", event);
  }
};
</script>
